import {
  all,
  put,
  call,
  takeEvery,
  takeLatest,
  select
} from 'redux-saga/effects'
import { push } from 'connected-react-router'

import {
  personAuth,
  restorePassword as restorePasswordRequest,
  changePassword as changePasswordRequest
} from '../api'
import { apiError, getToken, showPopup } from '../helpers'
import { captureException } from '../helpers/Sentry'
import { getRouter } from '../selectors'
import { setLoadTicketLogin } from './app'

export const initialState = {
  token: '',
  authorized: null,
  loading: false,
  initialized: false,
  error: null,
  restorePassword: {
    loading: false,
    error: false
  },
  changePassword: {
    loading: false,
    error: false
  }
}

export const moduleName = 'auth'
export const FETCH_AUTH_REQUEST = `${moduleName}/FETCH_AUTH_REQUEST`
export const FETCH_AUTH_SUCCESS = `${moduleName}/FETCH_AUTH_SUCCESS`
export const FETCH_AUTH_FAIL = `${moduleName}/FETCH_AUTH_FAIL`

export const RESTORE_PASSWORD_REQUEST = `${moduleName}/RESTORE_PASSWORD_REQUEST`
export const RESTORE_PASSWORD_SUCCESS = `${moduleName}/RESTORE_PASSWORD_SUCCESS`
export const RESTORE_PASSWORD_FAIL = `${moduleName}/RESTORE_PASSWORD_FAIL`

export const CHANGE_PASSWORD_REQUEST = `${moduleName}/CHANGE_PASSWORD_REQUEST`
export const CHANGE_PASSWORD_SUCCESS = `${moduleName}/CHANGE_PASSWORD_SUCCESS`
export const CHANGE_PASSWORD_FAIL = `${moduleName}/CHANGE_PASSWORD_FAIL`

export const CHECK_AUTH_REQUEST = `${moduleName}/CHECK_AUTH_REQUEST`
export const CHECK_AUTH_SUCCESS = `${moduleName}/CHECK_AUTH_SUCCESS`

export const LOGOUT = `${moduleName}/LOGOUT`

export default function reducer(state = initialState, action) {
  const { type, payload } = action

  switch (type) {
    case FETCH_AUTH_REQUEST:
      return { ...state, loading: true, error: null }
    case FETCH_AUTH_SUCCESS:
      return { ...state, loading: false, error: null, ...payload }
    case FETCH_AUTH_FAIL:
      return { ...state, loading: false, error: payload }
    case LOGOUT: {
      return { ...initialState, authorized: false, initialized: true }
    }

    case RESTORE_PASSWORD_REQUEST:
      return {
        ...state,
        restorePassword: { error: false, loading: true }
      }

    case RESTORE_PASSWORD_SUCCESS:
      return {
        ...state,
        restorePassword: {
          loading: false,
          error: false
        }
      }

    case RESTORE_PASSWORD_FAIL:
      return {
        ...state,
        restorePassword: {
          loading: false,
          error: payload
        }
      }

    case CHANGE_PASSWORD_REQUEST:
      return {
        ...state,
        changePassword: { error: false, loading: true }
      }

    case CHANGE_PASSWORD_SUCCESS:
      return {
        ...state,
        changePassword: {
          loading: false,
          error: false
        }
      }

    case CHANGE_PASSWORD_FAIL:
      return {
        ...state,
        changePassword: {
          loading: false,
          error: payload
        }
      }

    case CHECK_AUTH_REQUEST:
      return { ...state, initialized: true }
    case CHECK_AUTH_SUCCESS:
      return { ...state, ...payload }

    default:
      return state
  }
}

export function fetchAuth(values) {
  return {
    type: FETCH_AUTH_REQUEST,
    payload: values
  }
}

export function checkAuth() {
  return {
    type: CHECK_AUTH_REQUEST
  }
}

export function logout(withError = true) {
  return {
    type: LOGOUT,
    withError
  }
}

export function restorePassword(username) {
  return {
    type: RESTORE_PASSWORD_REQUEST,
    payload: username
  }
}

export function changePassword(payload) {
  return {
    type: CHANGE_PASSWORD_REQUEST,
    payload
  }
}

export const fetchAuthSaga = function*(action) {
  const { username, password, remember } = action.payload
  try {
    const { token } = yield call(personAuth, { username, password })

    if (remember) {
      yield call([localStorage, localStorage.setItem], 'token', token)
    }
    yield call([sessionStorage, sessionStorage.setItem], 'token', token)

    yield put({
      type: FETCH_AUTH_SUCCESS,
      payload: {
        authorized: true,
        token
      }
    })
    yield put(push('/dashboard'))
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_AUTH_FAIL,
        payload: {
          code,
          message
        }
      })
    } else {
      yield put({
        type: FETCH_AUTH_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const checkAuthSaga = function*() {
  try {
    const router = yield select(getRouter)

    const token = yield call(getToken)
    const { pathname } = router.location
    if (!token) {
      if (pathname.indexOf('/dashboard/interactions/') !== -1) {
        const ticketIdRegex = /(\d|\w){8}-(\d|\w){4}-(\d|\w){4}-(\d|\w){4}-(\d|\w){12}/gm

        const interactionId = ticketIdRegex.exec(pathname)[0] || ''
        yield put(setLoadTicketLogin(interactionId))
      }

      if (pathname === '/change-password' || pathname === '/restore-password') {
      } else {
        yield put(push('/'))
      }

      return yield put({
        type: CHECK_AUTH_SUCCESS,
        payload: {
          initialized: true,
          authorized: false
        }
      })
    }

    if (pathname === '/') {
      yield put(push('/dashboard'))
    }

    return yield put({
      type: CHECK_AUTH_SUCCESS,
      payload: {
        initialized: true,
        authorized: true,
        token
      }
    })
  } catch (e) {
    console.error(e)
  }
}

export const logoutSaga = function*({ withError = true }) {
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')
  yield put(push('/'))
  if (withError) {
    yield put(showPopup({ code: 2 }))
  }
}

export const restorePasswordSaga = function*(action) {
  try {
    const username = action.payload
    yield call(restorePasswordRequest, username)

    yield put({
      type: RESTORE_PASSWORD_SUCCESS
    })
    yield put(push('/'))
    // TODO success notification
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: RESTORE_PASSWORD_FAIL,
        payload: {
          code,
          message
        }
      })
    } else {
      yield put({
        type: RESTORE_PASSWORD_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const changePasswordSaga = function*(action) {
  try {
    let resetToken
    const { password, key } = action.payload

    if (key) {
      resetToken = key
    } else {
      const authToken = yield call(getToken)

      if (authToken) {
        resetToken = authToken
      } else {
        return yield put(logout(false))
      }
    }

    yield call(changePasswordRequest, password, resetToken)

    yield put({
      type: CHANGE_PASSWORD_SUCCESS
    })
    yield put(logout(false))
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: CHANGE_PASSWORD_FAIL,
        payload: {
          code,
          message
        }
      })
    } else {
      yield put({
        type: CHANGE_PASSWORD_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const saga = function*() {
  yield all([
    takeEvery(FETCH_AUTH_REQUEST, fetchAuthSaga),
    takeEvery(LOGOUT, logoutSaga),
    takeEvery(CHECK_AUTH_REQUEST, checkAuthSaga),
    takeLatest(RESTORE_PASSWORD_REQUEST, restorePasswordSaga),
    takeLatest(CHANGE_PASSWORD_REQUEST, changePasswordSaga)
  ])
}
